import { ReactElement, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { buildRatioTimeSeries, DataMode } from './auxSensorChannels';

interface AuxRatioTimePlotProps {
  gridded: Record<string, (number | null)[]> | undefined;
  circumference: number;
  /** Min linear speed (m/s) — samples below are masked (shared slider). */
  threshold: number;
  dataMode?: DataMode;
  isLoading?: boolean;
}

/**
 * Slip ratio (wheel speed 1 / wheel speed 2) over time. Samples below the shared
 * min-speed threshold are masked out (rendered as line gaps). A dashed reference
 * line marks ratio = 1 (sensor agreement).
 */
export default function AuxRatioTimePlot({
  gridded,
  circumference,
  threshold,
  dataMode = 'all',
  isLoading = false,
}: AuxRatioTimePlotProps): ReactElement {
  const points = useMemo(
    () => buildRatioTimeSeries(gridded, circumference, threshold, dataMode),
    [gridded, circumference, threshold, dataMode],
  );

  const hasData = useMemo(() => points.some((p) => p[1] != null), [points]);

  const chartSeries = useMemo(
    () => [{ name: 'Wheel 1 / Wheel 2', data: points }],
    [points],
  );

  const options = useMemo(
    () => ({
      chart: {
        type: 'line' as const,
        height: 320,
        animations: { enabled: false },
        toolbar: {
          show: true,
          tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
        },
        zoom: { enabled: true, type: 'xy' as const },
      },
      colors: ['#8B5CF6'],
      stroke: { curve: 'straight' as const, width: 1.5 },
      markers: { size: 0 },
      xaxis: {
        type: 'numeric' as const,
        title: { text: 'Time (seconds)', style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: string) => `${parseFloat(value).toFixed(0)}s` },
        decimalsInFloat: 0,
      },
      yaxis: {
        title: { text: 'Slip Ratio (Wheel 1 / Wheel 2)', style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
      },
      grid: { borderColor: '#E5E7EB', strokeDashArray: 5 },
      annotations: {
        yaxis: [{
          y: 1,
          borderColor: '#9CA3AF',
          strokeDashArray: 6,
          label: { text: 'ratio = 1', style: { color: '#6B7280', background: '#F9FAFB' } },
        }],
      },
      tooltip: {
        x: { formatter: (value: number) => `${value.toFixed(1)} s` },
        y: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
      },
    }),
    [],
  );

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
      <h2 className="cr-mb-2 cr-text-lg cr-font-semibold cr-text-blue-900">Slip Ratio vs Time</h2>
      {isLoading ? (
        <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-gap-2 cr-text-gray-400">
          <div
            className="cr-h-6 cr-w-6 cr-animate-spin cr-rounded-full cr-border-2
              cr-border-cirrusBlue-200 cr-border-t-cirrusBlue-400"
          />
          <span className="cr-text-sm">Loading…</span>
        </div>
      ) : !hasData ? (
        <div className="cr-flex cr-h-32 cr-items-center cr-justify-center cr-text-center cr-text-sm cr-text-gray-400">
          No samples above {threshold.toFixed(1)} m/s (needs two wheel sensors).
        </div>
      ) : (
        <ReactApexChart options={options} series={chartSeries} type="line" height={320} />
      )}
    </div>
  );
}
